import type { CaptionCue, CaptionWord } from './types';

export interface SegmentOptions {
  /** Short lines read best on phones. */
  readonly maxChars: number;
  readonly maxDurationSec: number;
  /** A pause longer than this always starts a new line. */
  readonly maxGapSec: number;
}

export const DEFAULT_SEGMENT_OPTIONS: SegmentOptions = { maxChars: 32, maxDurationSec: 3.2, maxGapSec: 0.6 };

const SENTENCE_END = /[.!?…。！？]["')\]]*$/;
const CLAUSE_END = /[,;:]["')\]]*$/;

/** Removes ASR artefacts: surrounding whitespace, empty tokens, inverted times. */
export function cleanWords(words: readonly CaptionWord[]): CaptionWord[] {
  return words
    .map((w) => ({ text: w.text.trim(), start: w.start, end: Math.max(w.end, w.start) }))
    .filter((w) => w.text.length > 0 && Number.isFinite(w.start) && Number.isFinite(w.end));
}

function cue(words: readonly CaptionWord[], index: number): CaptionCue {
  return {
    id: `cue-${index}-${Math.round(words[0]!.start * 1000)}`,
    start: words[0]!.start,
    end: words.at(-1)!.end,
    text: words.map((w) => w.text).join(' '),
    words: [...words],
  };
}

/**
 * Groups words into short caption lines: breaks after sentences, at pauses,
 * and before a line gets too long or too slow to read. Prefers breaking after
 * a comma when a line is already reasonably full.
 */
export function wordsToCues(input: readonly CaptionWord[], options: SegmentOptions = DEFAULT_SEGMENT_OPTIONS): CaptionCue[] {
  const words = cleanWords(input);
  const cues: CaptionCue[] = [];
  let line: CaptionWord[] = [];
  const flush = () => {
    if (line.length > 0) cues.push(cue(line, cues.length));
    line = [];
  };
  for (const word of words) {
    const previous = line.at(-1);
    if (previous) {
      const length = line.reduce((sum, w) => sum + w.text.length + 1, 0) + word.text.length;
      const tooLong = length > options.maxChars;
      const tooSlow = word.end - line[0]!.start > options.maxDurationSec;
      const pause = word.start - previous.end > options.maxGapSec;
      if (tooLong || tooSlow || pause) flush();
    }
    line.push(word);
    const chars = line.reduce((sum, w) => sum + w.text.length + 1, 0);
    if (SENTENCE_END.test(word.text) || (CLAUSE_END.test(word.text) && chars > options.maxChars * 0.55)) flush();
  }
  flush();
  return cues;
}

/** Re-times a cue after its text was edited, spreading new words over the old span. */
export function retimeCueText(original: CaptionCue, text: string): CaptionCue {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { ...original, text: '', words: [] };
  const span = Math.max(0.001, original.end - original.start);
  const weights = tokens.map((t) => t.length + 1);
  const total = weights.reduce((a, b) => a + b, 0);
  let cursor = original.start;
  const words = tokens.map((token, i) => {
    const duration = (weights[i]! / total) * span;
    const word = { text: token, start: cursor, end: cursor + duration };
    cursor += duration;
    return word;
  });
  return { ...original, text: tokens.join(' '), words };
}
